import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add Authorization header from localStorage as fallback when cookies fail
api.interceptors.request.use((config) => {
  return config;
});

// Auto-logout on 401 (expired/invalid token) — skip for auth endpoints
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config?.url?.includes("/auth/")
    ) {
      if (window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  }
);

// Proactive token refresh — extends session while user is active
let refreshTimer = null;
export const startTokenRefresh = () => {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(async () => {
    try {
      await api.post("/auth/refresh");
    } catch {
      clearInterval(refreshTimer);
    }
  }, 8 * 60 * 60 * 1000); // Refresh every 8 hours
};

export const stopTokenRefresh = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
};

// Auth
export const getMe = () => api.get("/auth/me");
export const logout = () => {
  stopTokenRefresh();
  return api.post("/auth/logout");
};

// Dashboard
export const getDashboardStats = (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get("/dashboard/stats", { params });
};
export const getDashboardRevenue = (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get("/dashboard/revenue", { params });
};

// Preferences (per-user UI settings)
export const getPreferences = () => api.get("/preferences");
export const updatePreferences = (partial) => api.put("/preferences", partial);

// Gear
export const listGear = () => api.get("/gear");
export const createGear = (data) => api.post("/gear", data);
export const updateGear = (id, data) => api.put(`/gear/${id}`, data);
export const deleteGear = (id) => api.delete(`/gear/${id}`);
export const getGearConflicts = () => api.get("/gear/conflicts");
export const getGearShortageForecast = (days = 30) => api.get("/gear/shortage-forecast", { params: { days } });
export const getGearAvailability = (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get("/gear/availability", { params });
};

// Crew
export const listCrew = () => api.get("/crew");
export const getCrewAvailability = (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get("/crew/availability", { params });
};
export const createCrew = (data) => api.post("/crew", data);
export const updateCrew = (id, data) => api.put(`/crew/${id}`, data);
export const deleteCrew = (id) => api.delete(`/crew/${id}`);
export const getCrewCalendar = (month) => api.get("/crew/calendar", { params: { month } });
export const getCrewEvents = (crewId) => api.get(`/crew/${crewId}/events`);
export const assignCrewToEvent = (crewId, eventId) => api.post(`/crew/${crewId}/assign-event`, { event_id: eventId });
export const unassignCrewFromEvent = (crewId, eventId) => api.delete(`/crew/${crewId}/unassign-event/${eventId}`);

// Clients
export const listClients = () => api.get("/clients");
export const createClient = (data) => api.post("/clients", data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Events
export const listEvents = () => api.get("/events");
export const createEvent = (data) => api.post("/events", data);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data);
export const deleteEvent = (id) => api.delete(`/events/${id}`);

// Event Tasks
export const getEventTasks = (eventId) => api.get(`/events/${eventId}/tasks`);
export const addEventTask = (eventId, data) => api.post(`/events/${eventId}/tasks`, data);
export const updateEventTask = (eventId, taskId, data) => api.put(`/events/${eventId}/tasks/${taskId}`, data);
export const deleteEventTask = (eventId, taskId) => api.delete(`/events/${eventId}/tasks/${taskId}`);
export const generateEventTasks = (eventId) => api.post(`/events/${eventId}/generate-tasks`);

// Team
export const listTeam = () => api.get("/team");
export const updateUserRole = (userId, role) => api.put(`/team/${userId}/role`, { role });
export const inviteTeamMember = (data) => api.post("/team/invite", data);
export const removeTeamMember = (userId) => api.delete(`/team/${userId}`);
export const updateUserCrewLink = (userId, crewId) => api.put(`/team/${userId}/crew-link`, { crew_id: crewId });

// Seed
export const seedData = () => api.post("/seed");

// Twilio Availability Checks
export const sendAvailabilityCheck = (eventId, opts) => api.post(`/events/${eventId}/send-availability-check`, opts);
export const sendIndividualCheck = (eventId, crewId, opts) => api.post(`/events/${eventId}/crew/${crewId}/send-check`, opts);
export const overrideCrewStatus = (eventId, crewId, status) => api.patch(`/events/${eventId}/crew-status/${crewId}`, { status });
export const getAvailabilityChecks = (eventId) => api.get(`/events/${eventId}/availability-checks`);
export const getAvailabilitySummary = () => api.get("/availability-checks/summary");
export const getTwilioStatus = () => api.get("/twilio/status");
export const configureTwilioWebhook = (baseUrl) => api.post("/twilio/configure-webhook", { base_url: baseUrl });

// QuickBooks Integration
export const getQBStatus = () => api.get("/quickbooks/status");
export const connectQB = () => api.get("/quickbooks/connect");
export const disconnectQB = () => api.post("/quickbooks/disconnect");
export const syncQB = () => api.post("/quickbooks/sync");
export const fetchQBEstimates = () => api.get("/quickbooks/estimates");

// Google Calendar Integration
export const getGCalStatus = () => api.get("/google-calendar/status");
export const connectGCal = () => api.get("/google-calendar/connect");
export const disconnectGCal = () => api.post("/google-calendar/disconnect");
export const syncGCal = () => api.post("/google-calendar/sync");
export const listGCalEvents = () => api.get("/google-calendar/events");

// Sync
export const syncAll = () => api.post("/sync/all");
export const getSyncStatus = () => api.get("/sync/status");
export const getUnmatchedEvents = () => api.get("/events/unmatched");
export const mergeEvents = (primaryId, secondaryId) => api.post("/events/merge", { primary_event_id: primaryId, secondary_event_id: secondaryId });

// Roles
export const listRoles = () => api.get("/roles");
export const createRole = (name) => api.post("/roles", { name });
export const deleteRole = (roleId) => api.delete(`/roles/${roleId}`);

// SMS Templates
export const listSmsTemplates = () => api.get("/sms-templates");
export const createSmsTemplate = (data) => api.post("/sms-templates", data);
export const updateSmsTemplate = (id, data) => api.put(`/sms-templates/${id}`, data);
export const setSmsTemplateDefault = (id) => api.post(`/sms-templates/${id}/set-default`);
export const deleteSmsTemplate = (id) => api.delete(`/sms-templates/${id}`);

// Event P&L
export const getEventPL = (params) => api.get("/dashboard/event-pl", { params });

// Digest Emails
export const getDigestStatus = () => api.get("/digest/status");
export const sendDigest = () => api.post("/digest/send");
export const previewDigest = (type) => api.post("/digest/preview", { type });
export const testDigest = (email, type) => api.post("/digest/test", { email, type });

// Notes
export const listNotes = () => api.get("/notes");
export const createNote = (data) => api.post("/notes", data);
export const updateNote = (noteId, data) => api.put(`/notes/${noteId}`, data);
export const deleteNote = (noteId) => api.delete(`/notes/${noteId}`);
export const getNotesByEvent = (eventId) => api.get(`/notes/by-event/${eventId}`);
export const shareNote = (noteId, data) => api.post(`/notes/${noteId}/share`, data);
export const unshareNote = (noteId, userId) => api.delete(`/notes/${noteId}/share/${userId}`);
export const uploadNoteImage = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/notes/upload-image", formData, { headers: { "Content-Type": "multipart/form-data" } });
};

export default api;
